import Avatar from 'react-avatar'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import http from '../../../../../shared/http'
import { TApi } from '../../../../../shared/const'
import { getFormattedDate } from '../../../../../shared/helpers'
import eventEmitter, { types } from '../../../../../shared/eventEmitter'

import styles from '../style.module.scss'

/**
 * Abschnitt „Anteilseigner:innen" im Firmenprofil.
 *
 * Wer Anteile gezeichnet hat, stand bis 09.08.2026 nur in der Ansicht der
 * Junior Company selbst. Die Verwaltung sah davon nichts — obwohl der Verkauf
 * der Anteile einer der Schritte im Fortschritt der Company ist und die
 * Anteilscheine bei den Dokumenten liegen, ohne dass dort jemand die Summe
 * zusammenzählt.
 *
 * Nur lesend: Angelegt und gelöscht wird bei der Junior Company. Der Klick auf
 * eine Zeile öffnet dasselbe Fenster, das auch die Schüler:innen sehen.
 *
 * Die Adressen kommen von aussen: Dieselbe Liste steht seit 23.08.2026 im
 * Unternehmensprofil der Lehrkraft, und dort antwortet die Admin-Route nicht.
 */
export default function SharesTab({ companyId, listUrl, downloadUrl }) {
	const { t } = useTranslation()

	const [shareholders, setShareholders] = useState([])
	const [total, setTotal] = useState(0)
	const [isLoading, setLoading] = useState(true)

	useEffect(() => {
		setLoading(true)
		http.get(listUrl(companyId))
			.then(({ data }) => {
				setShareholders(data.rows || [])
				setTotal(data.total || 0)
			})
			.finally(() => setLoading(false))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId])

	const openDetail = shareholder => {
		eventEmitter.emit(types.openShareholderDetail, {
			shareholder,
			// Die Erklärung liegt bei den Dokumenten der Company; wer sie
			// herunterladen darf, entscheidet das Backend (services/documents).
			downloadUrl
		})
	}

	return (
		<div className={styles.stack}>
			<section className={styles.block}>
				<div className={styles.blockHead}>
					<h3 className={styles.blockTitle}>
						{t('Company Shares')} ({shareholders.length})
					</h3>
					{!!shareholders.length && (
						<span className={styles.blockHint}>
							{t('Shares sold')}: {total} €
						</span>
					)}
				</div>
				<p className={styles.blockNote}>{t('Shareholders admin hint')}</p>

				{isLoading ? null : !shareholders.length ? (
					<div className={styles.emptyPanel}>{t('No shareholders yet')}</div>
				) : (
					<div className={styles.peopleGrid}>
						{shareholders.map(item => {
							const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim()

							return (
								<button
									key={item.id}
									type="button"
									title={t('Show details')}
									className={styles.shareholderCard}
									onClick={() => openDetail(item)}
								>
									<span className={styles.employeeProfile}>
										<Avatar className={styles.userAvatar} round size={32} name={fullName} />
										<p className={styles.name}>{fullName}</p>
									</span>
									<span className={styles.shareholderMeta}>
										<span className={styles.shareholderShare}>{item.companyShare}&nbsp;€</span>
										<span className={styles.shareholderDate}>
											{getFormattedDate(item.createdAt)}
										</span>
									</span>
								</button>
							)
						})}
					</div>
				)}
			</section>
		</div>
	)
}

SharesTab.defaultProps = {
	listUrl: TApi.ADMIN_GET_COMPANY_SHAREHOLDERS,
	downloadUrl: TApi.ADMIN_DOWNLOAD_DOCUMENT
}

SharesTab.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	// Beide bekommen den Dateinamen bzw. die Kennung und geben die Adresse
	// zurueck — dieselbe Form wie in `shared/const`.
	listUrl: PropTypes.func,
	downloadUrl: PropTypes.func
}
